import Image from "next/image";
import Link from "next/link";
import React, { Fragment, useState } from "react";
import { formatScore } from "../../../utils/wine";
import LockedScore, { isScoreMissing } from "../../global/Wines/elements/LockedScore";
import {
  defineRegionTextColor,
  WinesTopEntry,
} from "../../../utils/wineRanking";

interface WineTopContentProps {
  description: string;
  author?: string;
  issueDate?: string;
  tastedOn?: string;
  wines: WinesTopEntry[];
}

const WinesTopContent = ({ description, wines }: WineTopContentProps) => {
  const [openRow, setOpenRow] = useState<number | null>(null);

  const toggleRow = (rowIndex: number) => {
    setOpenRow(openRow === rowIndex ? null : rowIndex);
  };

  return (
    <div className="overflow-x-auto mb-10 mt-6 xs:w-full sm:w-full md:w-full lg:w-3/4 mx-auto">
      <div
        className="mb-10"
        dangerouslySetInnerHTML={{ __html: description }}
      />
      <table className="w-full border border-gray-200 table-fixed">
        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="p-1 border-b w-2">
              <span className="sr-only">Expand row</span>
            </th>
            <th className="p-3 border-b w-16 text-center">Rank</th>
            <th className="p-3 border-b w-3/5">Wine</th>
            <th className="p-3 border-b w-16 text-center">Score</th>
            <th className="p-3 border-b w-16 text-right">Price</th>
          </tr>
        </thead>
        <tbody>
          {wines.map((row, idx) => (
            <Fragment key={row.id}>
              <tr
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => toggleRow(idx)}
              >
                <td className="p-3 border-b text-blue-600 w-2">
                  <svg
                    className={`stroke-red transition-all transform ${
                      openRow === idx ? "rotate-180" : "rotate-0"
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </td>
                <td className="p-3 border-b text-center">{row.rank}</td>
                <td className="p-3 border-b">
                  <Link
                    href={`/wines/${row.slug}`}
                    onClick={(e) => e.stopPropagation()}
                    target="_blank"
                  >
                    {row.vintage === 0 ? <span>NV</span> : row.vintage}{" "}
                    {row.overwriteWineName
                      ? row.overwriteWineName
                      : `${row.producerName} ${row.wineName}`}
                  </Link>
                  {row.country && (
                    <span className={defineRegionTextColor(row.color)}>
                      {" "}
                      {row.country}
                    </span>
                  )}
                </td>
                <td className="p-3 border-b text-center">
                  {isScoreMissing(row.scoreRaw) ? <LockedScore /> : formatScore(row.scoreRaw)}
                </td>
                <td className="p-3 border-b text-right">
                  {row.price && row.price > 0
                    ? `$${Math.floor(row.price)}`
                    : " "}
                </td>
              </tr>

              {openRow === idx && (
                <tr>
                  <td colSpan={5} className="p-3 bg-gray-50 border-b align-top">
                    {/* Mobile: float image left, text wraps */}
                    <div className="block md:hidden">
                      {row.thumbnailUrl && (
                        <Image
                          src={row.thumbnailUrl}
                          alt={`${row.vintage === 0 ? "NV" : row.vintage} ${row.producerName} ${
                            row.overwriteWineName
                              ? row.overwriteWineName
                              : `${row.wineName} ${row.producerName}`
                          } thumbnail`}
                          width={120}
                          height={120}
                          className="rounded object-contain w-[120px] h-[120px] aspect-square float-left mr-4 mb-2"
                        />
                      )}
                      <div>
                        {/* Text will wrap around the floated image */}
                        {row.extraNote && (
                          <p className="opacity-60 italic mb-2">
                            {row.extraNote}
                          </p>
                        )}
                        <div
                          dangerouslySetInnerHTML={{ __html: row.tastingNote }}
                        />
                        <span className="opacity-60 italic text-sm">
                          {row.author}
                        </span>
                      </div>
                    </div>
                    {/* Desktop: flex layout */}
                    <div className="hidden md:flex items-start gap-6 animate-fade-in">
                      {row.thumbnailUrl && (
                        <Image
                          src={row.thumbnailUrl}
                          alt={`${row.vintage === 0 ? "NV" : row.vintage} ${row.producerName} ${
                            row.overwriteWineName
                              ? row.overwriteWineName
                              : `${row.wineName} ${row.producerName}`
                          } thumbnail`}
                          width={120}
                          height={120}
                          className="rounded object-contain w-[120px] h-[120px] aspect-square"
                        />
                      )}
                      <div>
                        {row.extraNote && (
                          <p className="opacity-60 italic mb-2">
                            {row.extraNote}
                          </p>
                        )}
                        <div
                          dangerouslySetInnerHTML={{ __html: row.tastingNote }}
                        />
                        <span className="opacity-60 italic text-sm">
                          {row.author}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WinesTopContent;
